// Global Instructions Rule Applied!
import React, { forwardRef, useImperativeHandle } from 'react'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FormTable } from '../FormTable/index.tsx'
import { useDealCommitmentsController } from './controller.js'
import { getFundColumnConfig } from './components/FundColumn.jsx'
import { getAmountColumnConfig } from './components/AmountColumn.jsx'

/**
 * @typedef {object} DealCommitmentsProps
 * @property {'new' | 'existing'} [mode]
 * @property {any[]} [initialCommitments]
 * @property {((commitments: any[]) => void) | null} [onChange]
 * @property {((commitments: any[]) => unknown) | null} [onSave]
 * @property {string} [currencySymbol]
 * @property {boolean} [showTotal]
 * @property {string | null} [recordId]
 */
export const DealCommitments = forwardRef(
  /**
   * @param {DealCommitmentsProps} props
   * @param {React.ForwardedRef<any>} ref
   */
  (
    {
      mode = 'new',
      initialCommitments = [],
      onChange = null,
      onSave = null,
      currencySymbol = '$',
      showTotal = true,
      recordId = null
    },
    ref
  ) => {
    const {
      commitments,
      editingRowKey,
      focusField,
      validationErrors,
      isAddingNewCommitment,
      savingRowKey,
      deletingRowKey,
      totalAmount,
      getAvailableFundOptions,
      showAllFunds,
      setShowAllFunds,
      hiddenFundCount,
      handleAddCommitment,
      handleRemoveCommitment,
      handleFieldChange,
      startEditing,
      cancelEditing,
      saveRow,
      hasPendingCommitments
    } = useDealCommitmentsController({
      mode,
      initialCommitments,
      onChange,
      onSave,
      currencySymbol,
      recordId
    })

    // Expose pending check function to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        hasPendingCommitments
      }),
      [hasPendingCommitments]
    )

    const columns = [
      getFundColumnConfig({
        mode,
        editingRowKey,
        focusField,
        validationErrors,
        getAvailableFundOptions,
        showAllFunds,
        setShowAllFunds,
        hiddenFundCount,
        handleFieldChange,
        startEditing
      }),
      getAmountColumnConfig({
        mode,
        editingRowKey,
        focusField,
        validationErrors,
        currencySymbol,
        handleFieldChange,
        startEditing
      })
    ]

    return (
      <div>
        <FormTable
          title='Our Commitment'
          dataSource={commitments}
          columns={columns}
          addButtonText=''
          addButtonIcon={faPlus}
          emptyText='No commitments'
          isAddingNew={isAddingNewCommitment}
          onAdd={handleAddCommitment}
          className='commitment-table'
          showHeader={false}
          // Actions column props
          includeActions={true}
          actionsWidth={60}
          mode={mode}
          editingRowKey={editingRowKey}
          savingRowKey={savingRowKey}
          deletingRowKey={deletingRowKey}
          onRemove={handleRemoveCommitment}
          onSave={saveRow}
          onCancel={cancelEditing}
          validateRecord={null}
          deleteConfirmTitle='Delete commitment'
          deleteConfirmDescription='Are you sure you want to remove this commitment?'
          useDeleteConfirm={mode === 'existing'}
          addButtonTestId='add-commitment-button'
          cardProps={{
            size: 'small',
            type: 'inner',
            styles: { body: { padding: 10 } }
          }}
        />

        {showTotal && commitments.length > 0 && (
          <div className='mt-2 text-right text-sm text-gray-600 mr-2'>
            <strong>
              Total: {currencySymbol}
              {Number(totalAmount).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </strong>
          </div>
        )}
      </div>
    )
  }
)

DealCommitments.displayName = 'DealCommitments'
